import { ExLoader } from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';
import ErrorAlertBanner from '../shared/ErrorAlertBanner';
import NoSearchResults from '../shared/NoSearchResults';
import SearchInput from '../shared/SearchInput';
import styles from './ConnectorList.module.css';
import ConnectorRow from './ConnectorRow';
import { type Connector, getConnectors } from './connectors';

interface ConnectorListProps {
    onSelect: (connectorId: string) => void;
}

const ConnectorList = ({ onSelect }: ConnectorListProps) => {
    const [connectors, setConnectors] = useState<Connector[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const data = await getConnectors();
                setConnectors(data.sort((a, b) => a.name.localeCompare(b.name)));
            } catch (err) {
                setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
            } finally {
                setIsLoading(false);
            }
        };

        load();
    }, []);

    const filteredConnectors = search
        ? connectors.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
        : connectors;

    return (
        <section className={styles.section}>
            {isLoading ? (
                <ExLoader label="Loading connectors…" />
            ) : error ? (
                <ErrorAlertBanner onClose={() => setError(null)}>{error}</ErrorAlertBanner>
            ) : (
                <>
                    <div className={styles.stickySearch}>
                        <SearchInput
                            data-pendo-input="createconfig-connectorlist-search"
                            value={search}
                            placeholder="Search connectors"
                            onSearch={setSearch}
                        />
                    </div>
                    {filteredConnectors.length === 0 ? (
                        <NoSearchResults itemLabel="connectors" search={search} />
                    ) : (
                        <div className={styles.track}>
                            {filteredConnectors.map((connector) => (
                                <ConnectorRow
                                    data-pendo-button="home-createconfig-connectorrow"
                                    data-pendo-connectorid={connector.id}
                                    key={connector.id}
                                    connector={connector}
                                    onViewConnections={onSelect}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

export default ConnectorList;
